import { useState, useEffect, useCallback } from 'react';
import { Box, Avatar, Chip, IconButton, Tooltip, LinearProgress } from '@mui/material';
import { Text } from '@qwickapps/react-framework';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExecutionStatusBar from './ExecutionStatusBar';
import ExecutionDetailsPanel from './ExecutionDetailsPanel';

interface PromptBubbleProps {
  content: string;
  timestamp: Date;
  executionStatus?: 'pending' | 'running' | 'retrying' | 'failed' | 'completed';
  errorMessage?: string;
  canRetry?: boolean;
  onRetry?: () => void;
  hasExecution?: boolean;
  inputTokens?: number;
  outputTokens?: number;
  createdAt?: Date;  // Execution creation time (for pending)
  startedAt?: Date;   // Execution start time (for running)
  completedAt?: Date; // Execution completion time
  // Enhanced metadata
  agentName?: string;
  modelName?: string;
  provider?: string;
  toolCalls?: { name: string; count: number }[];
  totalToolCalls?: number;
  costUsd?: number;
  deviceInfo?: {
    hostname?: string;
    cpuLoad?: number;
    latencyMs?: number;
  };
}

export default function PromptBubble({
  content,
  timestamp,
  executionStatus,
  errorMessage,
  canRetry,
  onRetry,
  hasExecution = true,
  inputTokens,
  outputTokens,
  createdAt,
  startedAt,
  completedAt,
  agentName,
  modelName,
  provider,
  toolCalls,
  totalToolCalls,
  costUsd,
  deviceInfo,
}: PromptBubbleProps) {
  const [expanded, setExpanded] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string | null>(null);

  // Calculate elapsed time in human-readable format
  // Use useCallback to prevent recreating this function on every render
  const calculateElapsedTime = useCallback(() => {
    // For pending executions, use createdAt; for running/completed, use startedAt
    const effectiveStart = startedAt || createdAt;
    if (!effectiveStart) return null;

    // IMPORTANT: Both dates must be in the same timezone
    // effectiveStart comes from DB (already parsed as Date object)
    // For ongoing executions, use current time
    const end = completedAt || new Date();

    // Use getTime() which always returns UTC milliseconds since epoch
    // This works regardless of timezone representation
    const ms = end.getTime() - effectiveStart.getTime();

    // Guard against clock skew or invalid timestamps
    if (ms < 0) {
      console.warn('[PromptBubble] Negative elapsed time detected:', {
        startedAt: startedAt.toISOString(),
        end: end.toISOString(),
        ms,
        startedAtMs: startedAt.getTime(),
        endMs: end.getTime()
      });
      return '0s';
    }

    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }, [startedAt, createdAt, completedAt]);

  // Live elapsed timer - updates every second for active executions
  useEffect(() => {
    const isActive = executionStatus && ['pending', 'running', 'retrying'].includes(executionStatus);

    if (isActive && (startedAt || createdAt)) {
      // Update immediately
      setElapsedTime(calculateElapsedTime());

      // Then update every second
      const interval = setInterval(() => {
        setElapsedTime(calculateElapsedTime());
      }, 1000);

      // CRITICAL: Cleanup to prevent memory leak
      return () => {
        clearInterval(interval);
      };
    } else {
      // For completed/failed, calculate once
      setElapsedTime(calculateElapsedTime());
    }
  }, [executionStatus, startedAt, createdAt, calculateElapsedTime]);

  // Auto-expand for running or failed executions
  const isActiveExecution = executionStatus && ['pending', 'running', 'retrying'].includes(executionStatus);
  const isFailedExecution = executionStatus === 'failed';
  const isCompletedExecution = executionStatus === 'completed';
  const hasExecutionDetails = isActiveExecution || isFailedExecution || isCompletedExecution;

  // Smart status inference based on execution state and age
  const getStatusChip = () => {
    // If we have execution status, use it
    if (hasExecution && executionStatus) {
      return null; // Status is handled by ExecutionStatusBar
    }

    // No execution - infer status from prompt age
    const ageMs = Date.now() - timestamp.getTime();
    const ageMinutes = ageMs / 1000 / 60;

    if (ageMinutes < 1) {
      return <Chip label="Pending..." size="small" color="default" sx={{ height: 20 }} />;
    } else if (ageMinutes < 5) {
      return (
        <Tooltip title="Device may be slow or busy processing">
          <Chip
            label="Waiting..."
            size="small"
            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 20,
              backgroundColor: 'var(--theme-warning)',
              color: 'var(--theme-on-warning)'
            }}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="No response received. Check if device is running and connected to the network.">
          <Chip
            label="No Reply"
            size="small"
            icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 20,
              backgroundColor: 'var(--theme-error)',
              color: 'var(--theme-on-error)'
            }}
          />
        </Tooltip>
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexDirection: 'row-reverse',
        mb: 2,
      }}
    >
      <Avatar
        sx={{
          bgcolor: 'var(--theme-primary-light)',
          width: 40,
          height: 40,
        }}
      >
        <PersonIcon sx={{ color: 'var(--theme-on-primary)' }} />
      </Avatar>

      <Box
        sx={{
          flex: 1,
          maxWidth: '80%',
        }}
      >
        {/* Header - outside the bubble */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
            flexDirection: 'row-reverse',
          }}
        >
          <Text variant="caption" customColor="var(--theme-text-secondary)" style={{ fontWeight: 600 }}>
            You
          </Text>
          <Text variant="caption" customColor="var(--theme-text-secondary)">
            {timestamp.toLocaleTimeString()}
          </Text>
          {canRetry && onRetry && (
            <Tooltip title="Retry execution">
              <IconButton size="small" onClick={onRetry} sx={{ ml: 1 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Prompt Bubble - contains everything */}
        <Box
          sx={{
            bgcolor: 'var(--theme-primary-light)',
            color: 'var(--theme-on-primary)',
            p: 2,
            borderRadius: 2,
            boxShadow: 1,
            border: isFailedExecution ? 2 : 'none',
            borderColor: 'var(--theme-error)',
          }}
        >
          {/* Status bar inside bubble */}
          {hasExecution && executionStatus ? (
            <ExecutionStatusBar
              executionStatus={executionStatus}
              elapsedTime={elapsedTime}
              hasExecutionDetails={hasExecutionDetails}
              expanded={expanded}
              onToggleExpand={() => setExpanded(!expanded)}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                pb: 1,
                borderBottom: 1,
                borderColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              {getStatusChip()}
            </Box>
          )}

          {/* Prompt Content */}
          <Text variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Text>

          {/* Progress bar for active executions */}
          {isActiveExecution && (
            <LinearProgress
              sx={{
                mt: 2,
                borderRadius: 1,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
          )}

          {/* Execution Details - Collapsible, inside bubble */}
          {hasExecutionDetails && (
            <ExecutionDetailsPanel
              expanded={expanded}
              agentName={agentName}
              modelName={modelName}
              provider={provider}
              inputTokens={inputTokens}
              outputTokens={outputTokens}
              costUsd={costUsd}
              toolCalls={toolCalls}
              totalToolCalls={totalToolCalls}
              deviceInfo={deviceInfo}
              executionStatus={executionStatus}
              errorMessage={errorMessage}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
